import React, { useState, useEffect, useRef } from 'react';

interface StepTwoProps {
  vehicleImages: File[];
  setVehicleImages: React.Dispatch<React.SetStateAction<File[]>>;
  registrationBookImages: File[];
  setRegistrationBookImages: React.Dispatch<React.SetStateAction<File[]>>;
  formErrors: any;
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    maxSizeMB: number,
    allowedTypes: string[]
  ) => void;
}

const StepTwo: React.FC<StepTwoProps> = ({
  vehicleImages,
  setVehicleImages,
  registrationBookImages,
  setRegistrationBookImages,
  formErrors,
  handleFileChange,
}) => {
  const [vehicleImagePreviews, setVehicleImagePreviews] = useState<string[]>([]);
  const [regBookImagePreviews, setRegBookImagePreviews] = useState<string[]>([]);
  const vehicleInputRef = useRef<HTMLInputElement>(null);
  const regBookInputRef = useRef<HTMLInputElement>(null);

  const maxFileSizeMB = 5; // Max 5MB per file
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

  // Update previews when files change
  const generatePreviews = (files: File[], setPreviews: React.Dispatch<React.SetStateAction<string[]>>) => {
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => {
      prev.forEach(URL.revokeObjectURL); // Clean up old previews
      return previews;
    });
  };

  // Handle file removal
  const removeFile = (
    index: number,
    files: File[],
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    previews: string[],
    setPreviews: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    updatedPreviews.forEach(URL.revokeObjectURL); // Clean up removed preview
  };

  // Trigger file input click
  const triggerFileInput = (inputRef: React.RefObject<HTMLInputElement>) => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  // Update previews when files change
  useEffect(() => {
    generatePreviews(vehicleImages, setVehicleImagePreviews);
    return () => vehicleImagePreviews.forEach(URL.revokeObjectURL); // Cleanup on unmount
  }, [vehicleImages]);

  useEffect(() => {
    generatePreviews(registrationBookImages, setRegBookImagePreviews);
    return () => regBookImagePreviews.forEach(URL.revokeObjectURL); // Cleanup on unmount
  }, [registrationBookImages]);

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="row" style={{    display: 'flex',flexDirection: 'row',gap: '40px',flexWrap: 'nowrap'}}>
          <div className="col-md-5  style={{ flex: 1 }}">
            <div className="mb-4 mt-4">
              <label className="col-form-label mb-2 ">
                <h5>Vehicle Images</h5>
              </label>
              <button
                
                type="button"
                className="btn btn-success"
                style={{marginLeft: '20px' , padding: '10px 15px', width: 'auto' ,height:'auto' }}
                onClick={() => triggerFileInput(vehicleInputRef)}
              >
                Add Images
              </button>
              <input
                type="file"
                ref={vehicleInputRef}
                style={{ display: 'none' }}
                multiple
                accept={allowedTypes.join(',')}
                onChange={(e) => {
                  handleFileChange(e, setVehicleImages, maxFileSizeMB, allowedTypes);
                  // ensure subsequent picks of the same files still trigger onChange
                  e.currentTarget.value = '';
                }}
              />

              <input
                type="file"
                ref={regBookInputRef}
                style={{ display: 'none' }}
                multiple
                accept={allowedTypes.join(',')}
                onChange={(e) => {
                  handleFileChange(e, setRegistrationBookImages, maxFileSizeMB, allowedTypes);
                  e.currentTarget.value = '';
                }}
              />
              <small className="text-muted d-block mt-1">
                Max {maxFileSizeMB}MB per file, formats: JPEG, PNG, JPG
              </small>
              {formErrors.vehicleImages && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.vehicleImages}
                </div>
              )}
              {vehicleImages.length > 0 && (
                <div className="mt-3">
                  <h6>Selected Files:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {vehicleImages.map((file, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={vehicleImagePreviews[index]}
                          alt={file.name}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0"
                          style={{ padding: '2px 6px' }}
                          onClick={() =>
                            removeFile(index, vehicleImages, setVehicleImages, vehicleImagePreviews, setVehicleImagePreviews)
                          }
                        >
                          ×
                        </button>
                        <div style={{ fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6  style={{ flex: 1 }}">
            <div className="mb-4 mt-4">
              <label className="col-form-label mb-2">
                <h5>Registration Book Images</h5>
              </label>
              <button
                type="button"
                className="btn btn-success"
                style={{ padding: '10px 15px', width: 'auto', height:'auto',marginLeft:'20px' }}
                onClick={() => triggerFileInput(regBookInputRef)}
              >
                Add Images
              </button>
              <input
                type="file"
                ref={regBookInputRef}
                style={{ display: 'none' }}
                multiple
                accept={allowedTypes.join(',')}
                onChange={(e) => handleFileChange(e, setRegistrationBookImages, maxFileSizeMB, allowedTypes)}
              />
              <small className="text-muted d-block mt-1">
                Max {maxFileSizeMB}MB per file, formats: JPEG, PNG, JPG
              </small>
              {formErrors.registrationBookImages && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.registrationBookImages}
                </div>
              )}
              {registrationBookImages.length > 0 && (
                <div className="mt-3">
                  <h6>Selected Files:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {registrationBookImages.map((file, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={regBookImagePreviews[index]}
                          alt={file.name}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0"
                          style={{ padding: '2px 6px' }}
                          onClick={() =>
                            removeFile(
                              index,
                              registrationBookImages,
                              setRegistrationBookImages,
                              regBookImagePreviews,
                              setRegBookImagePreviews
                            )
                          }
                        >
                          ×
                        </button>
                        <div style={{ fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepTwo;